import type { AccountState } from '../enums/account-state.enum';
import type { EmailAuthMethod } from '../enums/email.enum';

/**
 * The kinds of mailbox issue the Action items page lists — the mailboxes that
 * need the USER to act before (or so that) warmup can run.
 *
 * Deliberately NOT "every blocked state": a state that releases on its own
 * (spam / rate-driven bounce cool-down, a passing verification run) or that is
 * not a warmup state at all (`DISCONNECTED`) needs nothing from the user and
 * would only bury the real items.
 *
 * - `DNS_NOT_CONFIGURED`       — persisted DNS evidence fails the shared gate.
 * - `SENDING_FAILED`           — `sendCheckFailed`: the mailbox could not put a
 *                                message on the wire. See the note below on why
 *                                it needs its own type at all.
 * - `RECEIVING_ISOLATED`       — `receiveCheckFailed`: the mailbox cannot
 *                                receive, so it is barred from the recipient
 *                                pool. It KEEPS warming.
 * - `SUSPENDED_ACTION_REQUIRED`— suspended AND no auto-resume path exists.
 * - `BLACKLISTED`              — the domain, or the IP it was graded on, is
 *                                listed on ANY zone in `domainSecurityInfo`'s
 *                                stored breakdown (informational zones too —
 *                                the same evidence the detail page's
 *                                Blacklists section renders).
 *
 * ## Why `SENDING_FAILED` is not covered by `SUSPENDED_ACTION_REQUIRED`
 *
 * A send-check failure does NOT reliably produce a user-action suspension, so
 * without its own type the mailbox is invisible on this page in both of its
 * populations:
 *   - an `isMigrated` mailbox is exempt from the verification GATE (the checks
 *     still run and still write the flag), so it keeps warming in
 *     `WARMUP_ACTIVE` / `WARMUP_THROTTLED` and is never suspended at all;
 *   - a non-migrated one lands in `SUSPENDED_VERIFICATION`, which is
 *     deliberately absent from `USER_ACTION_SUSPENDED_STATES` because the
 *     auto-resume sweep releases it — but only once a run PASSES, which cannot
 *     happen until the user fixes the mailbox.
 * Both are therefore stuck behind something only the user can do, which is this
 * page's whole definition.
 */
export type ActionItemType =
  | 'DNS_NOT_CONFIGURED'
  | 'SENDING_FAILED'
  | 'RECEIVING_ISOLATED'
  | 'SUSPENDED_ACTION_REQUIRED'
  | 'BLACKLISTED';

/**
 * Runtime companion to {@link ActionItemType} — the one home for the wire value
 * the api puts on an item and the web copy registry keys on. Same
 * `as const satisfies` shape as `ACCOUNT_EVENT_TYPE`: a union member with no
 * constant, or a constant that is not a union member, fails `typecheck`.
 */
export const ACTION_ITEM_TYPE = {
  DNS_NOT_CONFIGURED: 'DNS_NOT_CONFIGURED',
  SENDING_FAILED: 'SENDING_FAILED',
  RECEIVING_ISOLATED: 'RECEIVING_ISOLATED',
  SUSPENDED_ACTION_REQUIRED: 'SUSPENDED_ACTION_REQUIRED',
  BLACKLISTED: 'BLACKLISTED',
} as const satisfies Record<ActionItemType, ActionItemType>;

/**
 * Every {@link ActionItemType}, in display order.
 *
 * ⚠ A type missing from this array does NOT fail `typecheck` — the array is
 * typed by its element, not by completeness. It is what `ActionItemsService`
 * builds the `counts` record from, so an omission makes that type's count
 * `NaN` (serialized as `null`) while every other number looks right. Pinned by
 * `packages/types/src/__tests__/action-items.spec.ts`.
 */
export const ACTION_ITEM_TYPES: ReadonlyArray<ActionItemType> = [
  ACTION_ITEM_TYPE.SUSPENDED_ACTION_REQUIRED,
  ACTION_ITEM_TYPE.BLACKLISTED,
  ACTION_ITEM_TYPE.DNS_NOT_CONFIGURED,
  ACTION_ITEM_TYPE.SENDING_FAILED,
  ACTION_ITEM_TYPE.RECEIVING_ISOLATED,
];

/** A DNS record that can block warmup (DMARC never gates, so it is absent). */
export type ActionItemDnsRecord = 'SPF' | 'MX' | 'DKIM';

export interface ActionItemDetail {
  /** `DNS_NOT_CONFIGURED` — the records the gate is blocking on. */
  blockingRecords?: ActionItemDnsRecord[];
  /** `SUSPENDED_ACTION_REQUIRED` — which suspension. */
  suspendState?: AccountState;
  /** `SUSPENDED_ACTION_REQUIRED` — the stored `warmupPausedReason`, verbatim. */
  suspendReason?: string | null;
  /** `SUSPENDED_ACTION_REQUIRED` — `permanentlyDisabled` (a dead-address bounce). */
  hardBounce?: boolean;
  /**
   * `SUSPENDED_ACTION_REQUIRED` only — which screen the fix action opens:
   * `OAUTH` (not DWD-managed, see `dwdWorkspaceId`) → the provider consent
   * redirect; `PASSWORD` → the SMTP reconnect form. Partner-managed mailboxes
   * never reach this page at all (excluded at the source — see
   * `classifyActionItem`), so there is no third value to represent here.
   */
  authMethod?: EmailAuthMethod;
  /**
   * `SUSPENDED_ACTION_REQUIRED` only — set (and `authMethod` is `OAUTH`) when
   * the mailbox was bulk-imported via domain-wide delegation (Google
   * Workspace or a Microsoft tenant); the fix action routes to that
   * workspace's Manage Mailboxes screen instead of a browser consent
   * redirect, since a DWD mailbox holds no per-mailbox OAuth to redirect.
   */
  dwdWorkspaceId?: number | null;
  /**
   * `SUSPENDED_ACTION_REQUIRED` only — true when the mailbox's tokens are
   * managed by a partner integration. Nothing in this UI can reconnect such a
   * mailbox (only the partner's own `POST /v2/accounts/oauth` call can), so
   * the fix action is a notice ("ask your org owner") rather than a
   * self-service Reconnect button, regardless of `authMethod`/`dwdWorkspaceId`.
   */
  partnerManaged?: boolean;
  /**
   * `SENDING_FAILED` only — what the failed send check MEANS for this mailbox
   * right now, which is not the same question as "did it fail".
   *
   * Three values because the page would otherwise state things that are false
   * for most of the fleet, and the two claims at risk are the two a user acts
   * on — why warmup is not running, and what makes it run again:
   *
   *  - `WARMING` — still sending. Only an `isMigrated` mailbox reaches this: it
   *    is exempt from the verification GATE while the checks still run and
   *    still write the flag, so it keeps warming at full volume with a send leg
   *    we know is broken. "Warmup has stopped" is a plain lie here.
   *  - `BLOCKED_BY_CHECK` — `SUSPENDED_VERIFICATION` or `MAILBOX_VERIFY_FAILED`:
   *    this check is the thing holding the mailbox, so a passing run really
   *    does release it. The ONLY value that may promise that.
   *  - `BLOCKED_OTHER` — not sending, for a reason this check does not own
   *    (a bounce suspension, a user pause, mid-verification). Warmup will NOT
   *    resume on a passing run, and such a mailbox is not even a daily
   *    health-check target, so the copy must claim neither.
   *
   * `BLOCKED_OTHER` is not hypothetical: it is where a `SUSPENDED_BOUNCE`
   * mailbox without `permanentlyDisabled` lands, which the suspended case
   * deliberately does not take (that suspension auto-resumes).
   */
  sendFailureContext?: 'WARMING' | 'BLOCKED_BY_CHECK' | 'BLOCKED_OTHER';
  /**
   * `SENDING_FAILED` only — the mailbox ALSO failed the receive check.
   *
   * The send case outranks the receive case (see `classifyActionItem`), so
   * without this the "cannot receive either" half of a doubly-broken mailbox
   * would simply vanish from the page when the send item took its row. Most
   * such mailboxes are dead addresses, where it is the single most useful fact
   * on the row.
   */
  receiveAlsoFailed?: boolean;
  /** `BLACKLISTED` only — zones listing the domain name (DBL / URI zones). */
  blacklistDomainZones?: string[];
  /** `BLACKLISTED` only — zones listing {@link blacklistIp} (RBL zones). */
  blacklistIpZones?: string[];
  /** `BLACKLISTED` only — the IPv4 the RBL zones were queried against. */
  blacklistIp?: string | null;
}

export interface ActionItem {
  emailAccountId: number;
  emailAddress: string;
  provider: string;
  type: ActionItemType;
  /**
   * ISO timestamp, or `null` when the type has no source timestamp.
   *
   * ⚠ **Nothing renders this any more — it is a SORT KEY, not a display field**
   * (user decision 2026-10-12). `ActionItemRow` showed a "when" per row until
   * the fourth type made the column half-empty: `SENDING_FAILED` is always
   * `null` here, so three types showed a date and one showed `—`, which read as
   * missing data rather than as a type that has no date. The column was dropped
   * for every type rather than filled in for one. `compareItems` still orders
   * the list by this value (nulls last), which is now the only thing it does.
   *
   * `null` is the DESIGNED value for `SENDING_FAILED`: unlike `receiveCheckFailed`
   * (whose `receiveCheckFailedAt` sibling column is stamped on the 0→1 flip),
   * `sendCheckFailed` has no "broken since" column and deliberately gains none
   * — the run's own history is already in `mailboxVerificationEmails` for
   * anyone who needs it. The DNS case's value is an approximation too
   * (`recordsRefreshedAt ?? createdAt`), which is the second reason showing it
   * was never worth much.
   *
   * It stays on the wire because it is the sort key and because a consumer may
   * want it; re-surfacing it in the UI means first answering what a
   * `SENDING_FAILED` row should display.
   */
  occurredAt: string | null;
  detail: ActionItemDetail;
}

/**
 * ORG-level misconfigurations of the daily sending budget — listed on the
 * Action items page above the per-mailbox rows. Not an {@link ActionItem}: they
 * belong to no mailbox, so they carry no `emailAccountId`, are not paginated,
 * and do not contribute to `counts`.
 *
 * - `ORG_SENDING_LIMIT_EXCEEDED` — the per-mailbox daily limits of the org's
 *   actively-warming mailboxes add up to more than the plan's
 *   `aggregateDailyWarmup`. The remedy is a bigger plan.
 * - `WORKSPACE_QUOTA_EXCEEDED`   — the workspaces' `dailyWarmupBudget`s add up
 *   to more than `aggregateDailyWarmup` (reachable after a plan downgrade —
 *   the quota PATCH itself refuses it). The remedy is re-allocating quotas.
 *
 * Neither is raised for an UNLIMITED (`null`) org limit.
 */
export type LimitAlertType = 'ORG_SENDING_LIMIT_EXCEEDED' | 'WORKSPACE_QUOTA_EXCEEDED';

export const LIMIT_ALERT_TYPE = {
  ORG_SENDING_LIMIT_EXCEEDED: 'ORG_SENDING_LIMIT_EXCEEDED',
  WORKSPACE_QUOTA_EXCEEDED: 'WORKSPACE_QUOTA_EXCEEDED',
} as const satisfies Record<LimitAlertType, LimitAlertType>;

export interface LimitAlert {
  type: LimitAlertType;
  /** The org's `aggregateDailyWarmup` — always a number here, never unlimited. */
  limit: number;
  /** What has been handed out against it: Σ mailbox limits or Σ workspace budgets. */
  allocated: number;
}

export interface ActionItemsResponse {
  items: ActionItem[];
  /** Org-level budget misconfigurations; empty when there are none. */
  limitAlerts: LimitAlert[];
  /** Items matching the active `type` filter. A lower bound when `truncated`. */
  total: number;
  /** Per-type counts over the whole scan — ignores the `type` filter. */
  counts: Record<ActionItemType, number>;
  /**
   * The candidate scan hit its cap, so `total` / `counts` are lower bounds. The
   * page must say so — a capped scan that reports plausible-but-low numbers is a
   * wrong figure nothing would ever flag.
   */
  truncated: boolean;
}
